import { Typography } from "@connected-repo/ui-mui/data-display/Typography";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { Stack } from "@connected-repo/ui-mui/layout/Stack";
import { activeNavIndex, navItemsForRole } from "@frontend/configs/nav.config";
import type { SessionInfo } from "@frontend/contexts/UserContext";
import { useStrings } from "@frontend/strings/useStrings";
import { alpha, ButtonBase, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link, useLoaderData, useLocation } from "react-router";
import { AccountMenu } from "./AccountMenu";
import { BrandMark } from "./BrandMark";

export const SIDEBAR_WIDTH = 248;
export const RAIL_WIDTH = 84;
export const MOBILE_TOP_BAR_HEIGHT = 60;

/** Short screens in landscape (phones turned sideways). */
export const SHORT_LANDSCAPE_QUERY = "(orientation: landscape) and (max-height: 520px)";

export type NavLayout = "sidebar" | "rail" | "mobile";

/**
 * Which navigation chrome fits the screen: the full sidebar on wide screens;
 * a slim icon rail on tablets in landscape and on phones held sideways (no
 * top / bottom bars eating the little height there is); the top bar +
 * bottom tabs on portrait phones and tablets.
 */
export const useNavLayout = (): NavLayout => {
	const theme = useTheme();
	const isLg = useMediaQuery(theme.breakpoints.up("lg"));
	const isMd = useMediaQuery(theme.breakpoints.up("md"));
	const shortLandscape = useMediaQuery(SHORT_LANDSCAPE_QUERY);
	if (isLg && !shortLandscape) return "sidebar";
	if (isMd || shortLandscape) return "rail";
	return "mobile";
};

const useNav = () => {
	const { t } = useStrings();
	const { pathname } = useLocation();
	const role = (useLoaderData() as SessionInfo | undefined)?.user?.role;
	const items = navItemsForRole(role);
	return { t, items, active: activeNavIndex(items, pathname) };
};

/**
 * Fixed left navigation with the brand, the sections and the signed-in user.
 * `compact` = the icon rail (labels under the icons, logo only, avatar only).
 */
export function Sidebar({ compact = false }: { compact?: boolean }) {
	const { t, items, active } = useNav();
	return (
		<Box
			component="nav"
			sx={{
				position: "fixed",
				inset: "var(--offline-bar, 0px) auto 0 0",
				width: compact ? RAIL_WIDTH : SIDEBAR_WIDTH,
				display: "flex",
				flexDirection: "column",
				alignItems: compact ? "center" : "stretch",
				px: compact ? 1 : 2,
				py: compact ? 1.5 : 2.5,
				bgcolor: "background.paper",
				borderRight: 1,
				borderColor: "divider",
				zIndex: (theme) => theme.zIndex.appBar,
			}}
		>
			<Box
				sx={{
					px: compact ? 0 : 1,
					mb: compact ? 1.5 : 4,
					minHeight: 40,
					display: "flex",
					alignItems: "center",
					justifyContent: compact ? "center" : "flex-start",
				}}
			>
				<BrandMark size={compact ? 40 : 36} withName={!compact} />
			</Box>
			<Stack spacing={compact ? 0.25 : 0.5} sx={{ flex: 1, minHeight: 0, overflowY: "auto", width: "100%", scrollbarWidth: "none" }}>
				{items.map((item, index) => {
					const selected = index === active;
					return (
						<ButtonBase
							key={item.path}
							component={Link}
							to={item.path}
							data-testid={`nav-${item.labelKey}`}
							aria-current={selected ? "page" : undefined}
							aria-label={compact ? t.nav[item.labelKey] : undefined}
							sx={{
								flexShrink: 0,
								flexDirection: compact ? "column" : "row",
								justifyContent: compact ? "center" : "flex-start",
								gap: compact ? 0.25 : 1.5,
								px: compact ? 0.5 : 1.5,
								height: compact ? 54 : 46,
								borderRadius: "12px",
								fontWeight: 700,
								fontSize: compact ? "0.66rem" : "0.95rem",
								lineHeight: 1.1,
								textAlign: "center",
								color: selected ? "primary.main" : "text.secondary",
								bgcolor: selected ? "primary.lighter" : "transparent",
								transition: "background-color 200ms, color 200ms",
								"&:hover": {
									bgcolor: selected ? "primary.lighter" : "action.hover",
									color: selected ? "primary.main" : "text.primary",
								},
								"& svg": { fontSize: 22 },
							}}
						>
							{item.icon}
							<Box component="span" sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
								{t.nav[item.labelKey]}
							</Box>
						</ButtonBase>
					);
				})}
			</Stack>
			<Box sx={{ pt: compact ? 1 : 2, borderTop: 1, borderColor: "divider", width: "100%", display: "flex", justifyContent: "center" }}>
				{compact ? <AccountMenu variant="avatar" /> : <AccountMenu variant="card" />}
			</Box>
		</Box>
	);
}

/** Mobile: slim translucent top bar (brand · account). */
export function MobileTopBar() {
	return (
		<Box
			component="header"
			sx={{
				position: "sticky",
				top: "var(--offline-bar, 0px)",
				zIndex: (theme) => theme.zIndex.appBar,
				height: MOBILE_TOP_BAR_HEIGHT,
				px: 2,
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				bgcolor: (theme) => alpha(theme.palette.background.default, 0.82),
				backdropFilter: "saturate(180%) blur(16px)",
				borderBottom: 1,
				borderColor: "divider",
			}}
		>
			<BrandMark size={32} />
			<Stack direction="row" alignItems="center" spacing={0.5}>
				<AccountMenu variant="avatar" />
			</Stack>
		</Box>
	);
}

/** Mobile: floating pill tab bar. */
export function MobileBottomNav() {
	const { t, items, active } = useNav();
	return (
		<Box
			component="nav"
			sx={{
				position: "fixed",
				left: 12,
				right: 12,
				bottom: "calc(12px + env(safe-area-inset-bottom))",
				zIndex: (theme) => theme.zIndex.appBar,
				display: "flex",
				p: 0.75,
				gap: 0.5,
				borderRadius: "20px",
				bgcolor: (theme) => alpha(theme.palette.mode === "light" ? "#0B0B0F" : "#1C1C23", 0.92),
				backdropFilter: "saturate(180%) blur(16px)",
				boxShadow: "0 18px 40px -16px rgba(0,0,0,0.55)",
			}}
		>
			{items.map((item, index) => {
				const selected = index === active;
				return (
					<ButtonBase
						key={item.path}
						component={Link}
						to={item.path}
						data-testid={`nav-${item.labelKey}`}
						aria-current={selected ? "page" : undefined}
						sx={{
							flex: 1,
							flexDirection: "column",
							gap: 0.25,
							py: 0.9,
							borderRadius: "16px",
							color: selected ? "#0B0B0F" : "rgba(255,255,255,0.62)",
							bgcolor: selected ? "#FFFFFF" : "transparent",
							transition: "background-color 220ms, color 220ms",
							"& svg": { fontSize: 22 },
						}}
					>
						{item.icon}
						<Typography sx={{ fontSize: "0.68rem", fontWeight: 700, lineHeight: 1.1 }} noWrap>
							{t.nav[item.labelKey]}
						</Typography>
					</ButtonBase>
				);
			})}
		</Box>
	);
}
